import { Link, Navigate, useParams } from 'react-router-dom';
import { Canvas } from '@/components/Canvas';
import { useAutosave } from '@/hooks/useAutosave';
import { useParamAnalytics } from '@/hooks/useParamAnalytics';
import { useScenarioSession } from '@/hooks/useScenarioSession';
import { isValidAccessCode } from './accessCode';

/**
 * One prospect's canvas, addressed by access code.
 *
 * The code is not a credential and there is no auth: it is an unguessable slug,
 * and the app holds zero PHI. What the code does control is whether a scenario
 * exists — and the canvas opens only when the service confirms that it does.
 *
 * The load, the fail-closed behavior and the two distinct refusals all live in
 * `useScenarioSession`, which the admin console shares. This component is the
 * URL half: read the code, refuse or render.
 */
export function ProspectCanvas() {
  const { accessCode } = useParams<{ accessCode: string }>();
  const valid = Boolean(accessCode && isValidAccessCode(accessCode));

  const { phase, retry } = useScenarioSession(valid && accessCode ? accessCode : null);

  /* Armed only once the load has resolved. Passing the raw code would let the
     750ms debounce beat a slow API and write whatever the store still held. */
  const activeScenario = valid && phase === 'ready' ? (accessCode ?? null) : null;
  useAutosave(activeScenario);
  useParamAnalytics(activeScenario !== null);

  if (!valid) return <Navigate to="/" replace />;

  if (phase === 'loading') {
    return (
      <main className="canvas canvas--loading">
        <p className="loading" role="status">
          Loading scenario…
        </p>
      </main>
    );
  }

  if (phase === 'unknown-code') {
    return (
      <main className="canvas canvas--loading">
        <div className="gate-notice" role="status">
          <h1>That code isn’t recognized</h1>
          {/* Says nothing about which codes do exist. */}
          <p>Check the link you were given, or try another code.</p>
          <Link className="gate-notice__action" to="/">
            Enter a different code
          </Link>
        </div>
      </main>
    );
  }

  if (phase === 'unreachable') {
    return (
      <main className="canvas canvas--loading">
        <div className="gate-notice" role="status">
          <h1>Can’t reach the scenario service</h1>
          {/* Deliberately not "unrecognized code": the code may be perfectly
              valid and we simply could not ask. */}
          <p>
            The code may be fine — the service just isn’t responding, so the
            canvas won’t open until it does.
          </p>
          <button type="button" className="gate-notice__action" onClick={retry}>
            Try again
          </button>
        </div>
      </main>
    );
  }

  return <Canvas scenarioId={accessCode ?? null} />;
}
